import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FileText } from "lucide-react";
import {
  deleteSensiReportUpload,
  fetchSensiReport,
  fetchSensiReportUploads,
  uploadSensiReport,
} from "../../../utils/api";
import SensiDropzone from "./components/SensiDropzone";
import SensiUploadTable from "./components/SensiUploadTable";
import SensiReportPreview from "./components/SensiReportPreview";
import { ACTIVE_STATUSES } from "./components/reportRow";

const MAX_FILE_MB = 25;
const MAX_FILE_BYTES = MAX_FILE_MB * 1024 * 1024;
// Uploads in flight at once; the rest wait in the list as "Queued".
const CONCURRENT_UPLOADS = 3;
// Reports per request in the upload list; older ones load on demand.
const HISTORY_PAGE_SIZE = 100;

const isPdf = (file) =>
  file.type === "application/pdf" || /\.pdf$/i.test(file.name || "");

const rejectionReason = (file) => {
  if (!isPdf(file)) return "Not a PDF";
  if (file.size === 0) return "File is empty";
  if (file.size > MAX_FILE_BYTES) return `Larger than ${MAX_FILE_MB} MB`;
  return "";
};

// Same name, size and modified time — the same file dropped twice.
const fileSignature = (file) => `${file.name}:${file.size}:${file.lastModified}`;

// A stored report is finished either way — the server extracts before it
// answers. "processing" describes only a file this browser is still sending, so
// it can't arrive from the server and wedge a row as permanently active.
const readServerStatus = (status, fallback) =>
  ["success", "failed"].includes(status) ? status : fallback;

const deleteErrorMessage = (err) =>
  err?.response?.data?.message || err?.message || "This report couldn't be deleted.";

const uploadErrorMessage = (err) => {
  if (err?.response?.status === 413) return "File is too large for the server.";
  if (err?.code === "ECONNABORTED") return "Upload timed out.";
  return err?.response?.data?.message || err?.message || "Upload failed.";
};

const fromSaved = (upload) => ({
  id: String(upload.id || upload._id),
  fileName: upload.fileName || "Untitled report",
  size: upload.fileSize ?? null,
  pageCount: upload.pageCount ?? null,
  uploadedAt: upload.uploadedAt || upload.createdAt || null,
  status: readServerStatus(upload.status, "success"),
  error: upload.error || "",
  progress: 100,
  retryable: false,
});

/**
 * SensiReport panel in a client's Integration tab: drag-and-drop upload of the
 * client's Sensi PDF reports into Albert. The backend reads each report into
 * JSON and discards the PDF; this panel only holds a file in memory until its
 * upload is answered.
 */
const SensiReportSection = ({ clientId }) => {
  const [rows, setRows] = useState([]);
  const [skipped, setSkipped] = useState([]);
  // Row keys with a delete in flight, so the cell can show it and a second
  // click can't send the same request twice.
  const [deleting, setDeleting] = useState(() => new Set());
  const [deleteError, setDeleteError] = useState("");
  // The row the preview dialog is open on, and what the server sent for it.
  const [preview, setPreview] = useState(null);
  const [previewData, setPreviewData] = useState({
    report: null,
    loading: false,
    error: "",
  });
  const [history, setHistory] = useState({
    uploads: [],
    total: 0,
    page: 1,
    loading: false,
    error: "",
  });
  // Uploads the server saved after this panel opened — used to recognise a file
  // that made it through even though the browser gave up waiting.
  const openedAtRef = useRef(Date.now());

  // key → { file, clientId }, dropped as soon as Albert has the report data.
  // Only failed uploads keep their file, so they can be retried.
  const filesRef = useRef(new Map());
  const signaturesRef = useRef(new Set());
  const queueRef = useRef([]);
  const activeRef = useRef(0);
  const seqRef = useRef(0);
  const mountedRef = useRef(true);
  const pumpRef = useRef(() => {});

  const updateRow = useCallback((key, patch) => {
    setRows((prev) =>
      prev.map((row) => (row.key === key ? { ...row, ...patch } : row)),
    );
  }, []);

  // `page` 1 replaces the list; a later page appends to it, which is what the
  // "Load older reports" button asks for.
  const loadHistory = useCallback(
    async (page = 1) => {
      if (!clientId) return;
      setHistory((prev) => ({ ...prev, loading: true, error: "" }));
      try {
        const data = await fetchSensiReportUploads(clientId, {
          page,
          limit: HISTORY_PAGE_SIZE,
        });
        const uploads = Array.isArray(data?.uploads) ? data.uploads : [];
        setHistory((prev) => ({
          uploads: page > 1 ? [...prev.uploads, ...uploads] : uploads,
          total: Number(data?.total) || uploads.length,
          page,
          loading: false,
          error: "",
        }));
      } catch {
        setHistory((prev) => ({
          ...prev,
          loading: false,
          error: "Earlier uploads couldn't be loaded. Uploads from this visit are still listed.",
        }));
      }
    },
    [clientId],
  );

  useEffect(() => {
    loadHistory(1);
  }, [loadHistory]);

  const runUpload = useCallback(
    async (key, { file, clientId: uploadClientId }) => {
      updateRow(key, { status: "uploading", progress: 0, error: "" });
      try {
        const result = await uploadSensiReport(uploadClientId, file, {
          // Once every byte is sent the server is reading the report.
          onProgress: (progress) =>
            updateRow(
              key,
              progress >= 100 ? { status: "processing", progress } : { progress },
            ),
        });
        // Albert has answered for this file either way — a report it couldn't
        // read won't read on a retry, so the PDF is released here too.
        filesRef.current.delete(key);
        const status = readServerStatus(result?.status, "success");
        updateRow(key, {
          id: result?.id || result?._id ? String(result.id || result._id) : null,
          status,
          progress: 100,
          pageCount: result?.pageCount ?? null,
          uploadedAt: result?.uploadedAt || new Date().toISOString(),
          error:
            status === "failed"
              ? result?.error || "Albert couldn't read this report."
              : "",
          retryable: false,
        });
      } catch (err) {
        updateRow(key, {
          status: "failed",
          error: uploadErrorMessage(err),
          retryable: true,
        });
      }
    },
    [updateRow],
  );

  useEffect(() => {
    pumpRef.current = () => {
      while (
        mountedRef.current &&
        activeRef.current < CONCURRENT_UPLOADS &&
        queueRef.current.length
      ) {
        const key = queueRef.current.shift();
        const entry = filesRef.current.get(key);
        if (!entry) continue;
        activeRef.current += 1;
        runUpload(key, entry).finally(() => {
          activeRef.current -= 1;
          if (!mountedRef.current) return;
          pumpRef.current();
          // Once the batch settles, the saved list is the record of what Albert kept.
          if (activeRef.current === 0 && queueRef.current.length === 0) {
            loadHistory();
          }
        });
      }
    };
  });

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      // Uploads already in flight finish on the server; queued ones are dropped.
      mountedRef.current = false;
      queueRef.current = [];
    };
  }, []);

  const addFiles = useCallback((fileList) => {
    const accepted = [];
    const rejected = [];
    Array.from(fileList || []).forEach((file) => {
      const signature = fileSignature(file);
      const reason =
        rejectionReason(file) ||
        (signaturesRef.current.has(signature) ? "Already added" : "");
      if (reason) {
        rejected.push({ name: file.name, reason });
        return;
      }
      const key = `local-${++seqRef.current}`;
      signaturesRef.current.add(signature);
      filesRef.current.set(key, { file, clientId });
      queueRef.current.push(key);
      accepted.push({
        key,
        signature,
        id: null,
        fileName: file.name,
        size: file.size,
        pageCount: null,
        uploadedAt: new Date().toISOString(),
        status: "queued",
        progress: 0,
        error: "",
        retryable: false,
      });
    });

    setSkipped(rejected);
    if (!accepted.length) return;
    setRows((prev) => [...accepted, ...prev]);
    pumpRef.current();
  }, [clientId]);

  const retryRow = useCallback((key) => {
    if (!filesRef.current.has(key)) return;
    queueRef.current.push(key);
    updateRow(key, { status: "queued", progress: 0, error: "", retryable: false });
    pumpRef.current();
  }, [updateRow]);

  const openPreview = useCallback(
    async (row) => {
      setPreview(row);
      setPreviewData({ report: null, loading: true, error: "" });
      try {
        const report = await fetchSensiReport(clientId, row.id);
        setPreviewData({ report, loading: false, error: "" });
      } catch (err) {
        setPreviewData({
          report: null,
          loading: false,
          error:
            err?.response?.data?.message ||
            "This report couldn't be loaded.",
        });
      }
    },
    [clientId],
  );

  const closePreview = useCallback(() => {
    setPreview(null);
    setPreviewData({ report: null, loading: false, error: "" });
  }, []);

  // Frees the file so the same PDF can be dropped in again. A queued key left
  // behind in the queue is skipped by the pump, which reads the file map.
  const forgetRow = useCallback((row) => {
    signaturesRef.current.delete(row.signature);
    filesRef.current.delete(row.key);
    setRows((prev) => prev.filter((r) => r.key !== row.key));
  }, []);

  /**
   * Removes a report from the list. One that never reached Albert is simply
   * dropped here; one Albert stored is deleted there first, or it would be back
   * on the next refresh. The server keeps the extracted report and only hides
   * it, and releases its duplicate check, so the same PDF can be uploaded again.
   */
  const deleteRow = useCallback(
    async (row) => {
      if (!row.id) {
        forgetRow(row);
        return;
      }
      setDeleting((prev) => new Set(prev).add(row.key));
      setDeleteError("");
      try {
        await deleteSensiReportUpload(clientId, row.id);
      } catch (err) {
        // Including a 404. It usually means someone else deleted the report
        // first, but it is also what a server without this route answers, and
        // dropping the row on either would claim a deletion that may not have
        // happened — Refresh settles which it was.
        setDeleteError(deleteErrorMessage(err));
        return;
      } finally {
        setDeleting((prev) => {
          const next = new Set(prev);
          next.delete(row.key);
          return next;
        });
      }
      forgetRow(row);
      setHistory((prev) => ({
        ...prev,
        uploads: prev.uploads.filter(
          (upload) => String(upload.id || upload._id) !== row.id,
        ),
        total: Math.max(0, prev.total - 1),
      }));
    },
    [clientId, forgetRow],
  );

  // This visit's uploads first, then everything saved earlier. A saved record
  // is the source of truth for an upload that has finished.
  const allRows = useMemo(() => {
    const saved = new Map(
      history.uploads.map((upload) => {
        const row = fromSaved(upload);
        return [row.id, row];
      }),
    );
    // A file the server stored during this visit, by name and size. If the
    // request timed out client-side the upload still counted, and the failed
    // row left behind would otherwise sit next to the saved one with a Retry
    // button the server would only answer with "already uploaded".
    const savedThisVisit = new Set(
      [...saved.values()]
        .filter((row) => new Date(row.uploadedAt).getTime() >= openedAtRef.current)
        .map((row) => `${row.fileName}:${row.size ?? ""}`),
    );
    const session = rows.flatMap((row) => {
      if (
        !row.id &&
        row.status === "failed" &&
        savedThisVisit.has(`${row.fileName}:${row.size ?? ""}`)
      ) {
        return [];
      }
      const match = row.id && saved.get(row.id);
      if (!match) return [row];
      saved.delete(row.id);
      return [{ ...row, ...match, key: row.key, size: row.size }];
    });
    const earlier = [...saved.values()].map((row) => ({
      ...row,
      key: `saved-${row.id}`,
    }));
    return [...session, ...earlier];
  }, [rows, history.uploads]);

  const sessionCounts = useMemo(
    () =>
      rows.reduce(
        (acc, row) => {
          if (ACTIVE_STATUSES.has(row.status)) acc.active += 1;
          else if (row.status === "failed") acc.failed += 1;
          else acc.done += 1;
          return acc;
        },
        { active: 0, done: 0, failed: 0 },
      ),
    [rows],
  );
  const uploading = sessionCounts.active > 0;

  // Leaving mid-batch would drop the files still waiting their turn.
  useEffect(() => {
    if (!uploading) return undefined;
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [uploading]);

  // A PDF dropped just outside the drop zone would otherwise open in the tab
  // and replace the app.
  useEffect(() => {
    const block = (e) => {
      if (Array.from(e.dataTransfer?.types || []).includes("Files")) {
        e.preventDefault();
      }
    };
    window.addEventListener("dragover", block);
    window.addEventListener("drop", block);
    return () => {
      window.removeEventListener("dragover", block);
      window.removeEventListener("drop", block);
    };
  }, []);

  const sessionTotal = rows.length;
  const sessionSettled = sessionCounts.done + sessionCounts.failed;

  return (
    <div className="flex flex-col gap-4 text-slate-800">
      <div className="flex items-start gap-3">
        <div className="grid h-[38px] w-[38px] shrink-0 place-items-center rounded-[10px] bg-violet-100 text-violet-600">
          <FileText size={18} aria-hidden="true" />
        </div>
        <div>
          <h2 className="m-0 text-[17px] font-bold tracking-tight">SensiReport</h2>
          <p className="mt-0.5 max-w-[60ch] text-[13px] text-slate-500">
            Upload this client's Sensi PDF reports and follow each one into Albert.
          </p>
        </div>
      </div>

      {/* Upload first, then every report's status below it. */}
      <div className="flex flex-col gap-4">
        <section aria-label="Upload reports" className="space-y-4">
          <SensiDropzone onFiles={addFiles} maxFileMb={MAX_FILE_MB} />

          {skipped.length ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
              <div className="flex items-start justify-between gap-3">
                <p className="text-sm font-medium text-amber-900">
                  {skipped.length === 1
                    ? "1 file wasn't added"
                    : `${skipped.length} files weren't added`}
                </p>
                <button
                  type="button"
                  onClick={() => setSkipped([])}
                  className="text-xs font-medium text-amber-800 hover:underline"
                >
                  Dismiss
                </button>
              </div>
              <ul className="mt-1 space-y-0.5 text-sm text-amber-900">
                {skipped.map((item, i) => (
                  <li key={`${item.name}-${i}`} className="break-all">
                    <span className="font-medium">{item.name}</span> — {item.reason}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {sessionTotal ? (
            <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
              <p className="text-sm font-medium text-slate-800">
                {uploading
                  ? `Uploading ${sessionCounts.active} of ${sessionTotal} file${sessionTotal === 1 ? "" : "s"}…`
                  : `${sessionTotal} file${sessionTotal === 1 ? "" : "s"} uploaded this visit`}
              </p>
              <p className="mt-0.5 text-xs text-slate-500">
                {sessionCounts.done} successful · {sessionCounts.failed} failed
                {uploading ? " · keep this page open until uploads finish" : ""}
              </p>
              <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-100">
                <div
                  className={`h-full rounded-full transition-all ${
                    sessionCounts.failed && !uploading ? "bg-amber-500" : "bg-blue-600"
                  }`}
                  style={{ width: `${(sessionSettled / sessionTotal) * 100}%` }}
                />
              </div>
            </div>
          ) : null}
        </section>

        <section aria-label="Upload status" className="min-w-0">
          <SensiUploadTable
            rows={allRows}
            loading={history.loading}
            error={history.error}
            hasMore={history.uploads.length < history.total}
            onLoadMore={() => loadHistory(history.page + 1)}
            onRefresh={() => loadHistory(1)}
            onRetry={retryRow}
            onPreview={openPreview}
            onDelete={deleteRow}
            deletingKeys={deleting}
            deleteError={deleteError}
          />
        </section>
      </div>

      {preview ? (
        <SensiReportPreview
          row={preview}
          report={previewData.report}
          loading={previewData.loading}
          error={previewData.error}
          onClose={closePreview}
        />
      ) : null}
    </div>
  );
};

export default SensiReportSection;
